import React, { useEffect, useMemo } from 'react';
import { useQuery } from 'react-query';
import { queryOptions } from '~/shared/constants';
import styled from 'styled-components';
import { fetchIndicatorData } from '~/shared/services/api';
import { Table } from '~/shared/components';
import { Loader } from '~/shared/components';
import { formatSparklineSeries, resolveSparklineSeries } from '~/shared/components/Table/charts/parseSparklineValues';

const Spinner = styled(Loader)`
  position: unset;
`;

const SpinnerWrapper = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 100%;
  width: 100%;
`;

const Wrapper = styled.div`
  height: 400px;
  width: 100%;
  position: relative;
  padding: 0 38px;
  margin-top: 10px;
  background: white;
  // box-shadow: 0px 2px 1px -1px rgb(0 0 0 / 20%), 0px 1px 1px 0px rgb(0 0 0 / 14%), 0px 1px 3px 0px rgb(0 0 0 / 12%);
  border: 1px solid #e6e9eb;
`;

export default function IndicatorTable({
  category,
  prism,
  prismId,
  indicator,
  cellInfo,
  filterValues,
  fetchIndicatorDataOverride,
}: any) {
  const { data: indicatorResponse, isLoading } = useQuery(
    ['fetchIndicatorData', indicator, cellInfo, JSON.stringify(filterValues)],
    async () => {
      const fetchFn = fetchIndicatorDataOverride ?? fetchIndicatorData;
      return fetchFn(category, prism, prismId, indicator, cellInfo, filterValues);
    },
    queryOptions,
  );

  type LinkValue = { text: string; url?: string };
  type FormattingResult = {
    dataKeys: string[];
    dataTypes: (string | undefined)[];
    tableData: Record<string, unknown>[];
    tableTitle: string;
    unresolved: { rowIndex: number; column: string; value: unknown }[];
  };

  const { dataKeys, dataTypes, tableData, tableTitle, unresolved } = useMemo<FormattingResult>(() => {
    const canonicalizeType = (input: string | undefined): string | undefined => {
      if (!input || typeof input !== 'string') {
        return undefined;
      }
      const normalized = input.trim().toLowerCase();
      switch (normalized) {
        case 'percent':
        case 'percentage':
        case 'pct':
          return 'Percent';
        case 'posnegpercent':
        case 'pos_neg_percent':
          return 'PosNegPercent';
        case 'posnegpercentbg':
        case 'pos_neg_percent_bg':
          return 'PosNegPercentBG';
        case 'posnegbarchart':
        case 'pos_neg_bar_chart':
        case 'posnegbar':
          return 'PosNegBarChart';
        case 'sparkline':
          return 'Sparkline';
        case 'number':
        case 'float':
        case 'double':
        case 'integer':
        case 'int':
        case 'decimal':
        case 'numberfloat':
          return 'NumberFloat';
        case 'link':
        case 'url':
          return 'link';
        case 'button':
          return 'button';
        default:
          return input;
      }
    };

    const normalizeLinkValue = (value: unknown): LinkValue => {
      if (value === null || value === undefined) {
        return { text: '', url: undefined };
      }

      if (typeof value === 'string' || typeof value === 'number') {
        const strValue = String(value);
        const isUrl = /^https?:\/\//i.test(strValue);
        return { text: strValue, url: isUrl ? strValue : undefined };
      }

      if (typeof value === 'object') {
        const possible = value as {
          text?: unknown;
          title?: unknown;
          label?: unknown;
          name?: unknown;
          value?: unknown;
          url?: unknown;
          href?: unknown;
          link?: unknown;
        };

        const possibleUrl = possible.url ?? possible.href ?? possible.link;
        const urlString =
          typeof possibleUrl === 'string' || typeof possibleUrl === 'number' ? String(possibleUrl) : undefined;

        const possibleText = possible.text ?? possible.title ?? possible.label ?? possible.name ?? possible.value;
        const textString =
          possibleText === null || possibleText === undefined
            ? (urlString ?? '')
            : typeof possibleText === 'string' || typeof possibleText === 'number'
              ? String(possibleText)
              : (urlString ?? '');

        return { text: textString, url: urlString };
      }

      return { text: String(value), url: undefined };
    };

    const looksLikeLinkObject = (value: unknown) => {
      if (!value || typeof value !== 'object') return false;
      const candidate = value as Record<string, unknown>;
      if ('url' in candidate || 'href' in candidate || 'link' in candidate) return true;
      if ('text' in candidate && typeof candidate.text === 'string' && /^https?:\/\//i.test(candidate.text))
        return true;
      return false;
    };

    if (!indicatorResponse || !Array.isArray(indicatorResponse.col_nodes) || !Array.isArray(indicatorResponse.data)) {
      return {
        dataKeys: [],
        dataTypes: [],
        tableData: [],
        tableTitle: indicatorResponse?.title ?? '',
        unresolved: [],
      };
    }

    const dataKeys = indicatorResponse.col_nodes.map((c: any) =>
      typeof c?.name === 'string' ? c.name.replace(' ', '_') : '',
    );
    const columnTypes = indicatorResponse.col_nodes.map((c: any) =>
      canonicalizeType(typeof c?.type === 'string' ? c.type : undefined),
    );
    const derivedTypes = [...columnTypes];
    const unresolvedObjectCells: { rowIndex: number; column: string; value: unknown }[] = [];

    const tableData = indicatorResponse.data.map((row: any, rowIndex: number) => {
      const formattedRow: Record<string, unknown> = {};
      if (!Array.isArray(row)) return formattedRow;

      row.forEach((cell, colIndex) => {
        const columnKey = dataKeys[colIndex];
        if (!columnKey) return;

        const normalizedType = columnTypes[colIndex]?.trim().toLowerCase();
        const isLinkColumn = normalizedType === 'link';
        const inferredLink = looksLikeLinkObject(cell);

        if (isLinkColumn || inferredLink) {
          const { text, url } = normalizeLinkValue(cell);
          formattedRow[columnKey] = text;
          if (url) {
            formattedRow[`${columnKey}__url`] = url;
          }
          if (!derivedTypes[colIndex] || derivedTypes[colIndex]?.toLowerCase() !== 'link') {
            derivedTypes[colIndex] = 'link';
          }
          return;
        }

        if (cell === null || cell === undefined) {
          formattedRow[columnKey] = '';
          return;
        }

        const isSparklineColumn = normalizedType === 'sparkline';
        const resolvedSeries = resolveSparklineSeries(cell);
        if (resolvedSeries && resolvedSeries.length > 1) {
          formattedRow[`${columnKey}_Components`] = resolvedSeries;
          formattedRow[`${columnKey}__series`] = formatSparklineSeries(resolvedSeries);
        }

        if (isSparklineColumn && resolvedSeries && resolvedSeries.length) {
          const lastValue = [...resolvedSeries].reverse().find(value => value !== null);
          formattedRow[columnKey] = lastValue ?? '';
          return;
        }

        if (Array.isArray(cell)) {
          if (!formattedRow[`${columnKey}__series`]) {
            formattedRow[columnKey] = cell
              .map(value => {
                if (value === null || value === undefined) return '';
                const num = Number(value);
                return Number.isNaN(num) ? '' : num;
              })
              .join(',');
            return;
          }
          formattedRow[columnKey] = formattedRow[`${columnKey}__series`];
          return;
        }

        if (typeof cell === 'string') {
          const trimmed = cell.trim();
          const percentMatch = /^-?\d+(?:\.\d+)?%$/.test(trimmed);
          if (percentMatch) {
            const numericValue = Number(trimmed.replace('%', ''));
            if (!Number.isNaN(numericValue)) {
              formattedRow[columnKey] = numericValue;
              derivedTypes[colIndex] = derivedTypes[colIndex] ?? 'Percent';
              return;
            }
          }

          const numericCandidate = trimmed.replace(/,/g, '');
          const isNumeric = /^-?\d+(?:\.\d+)?$/.test(numericCandidate);
          if (isNumeric) {
            const numericValue = Number(numericCandidate);
            if (!Number.isNaN(numericValue)) {
              formattedRow[columnKey] = numericValue;
              derivedTypes[colIndex] = derivedTypes[colIndex] ?? 'NumberFloat';
              return;
            }
          }
        }

        if (typeof cell === 'number') {
          formattedRow[columnKey] = cell;
          derivedTypes[colIndex] = derivedTypes[colIndex] ?? 'NumberFloat';
          return;
        }

        if (typeof cell === 'object') {
          unresolvedObjectCells.push({ rowIndex, column: columnKey, value: cell });
        }

        formattedRow[columnKey] = String(cell);
      });

      return formattedRow;
    });

    dataKeys.forEach((key: string, colIndex: number) => {
      if (derivedTypes[colIndex]) {
        derivedTypes[colIndex] = canonicalizeType(derivedTypes[colIndex]);
        return;
      }

      const columnValues = tableData
        .map((row: Record<string, unknown>) => row[key])
        .filter((value: unknown) => value !== '' && value !== undefined);
      const hasValues = columnValues.length > 0;
      const allNumeric = hasValues && columnValues.every((value: unknown) => typeof value === 'number');
      if (allNumeric) {
        derivedTypes[colIndex] = 'NumberFloat';
        return;
      }

      const allPercentStrings =
        hasValues &&
        columnValues.every(
          (value: unknown) => typeof value === 'string' && /^-?\d+(?:\.\d+)?%$/.test((value as string).trim()),
        );
      if (allPercentStrings) {
        derivedTypes[colIndex] = 'Percent';
        tableData.forEach((row: Record<string, unknown>) => {
          const raw = row[key];
          if (typeof raw === 'string') {
            const numericValue = Number(raw.replace('%', ''));
            if (!Number.isNaN(numericValue)) {
              row[key] = numericValue;
            }
          }
        });
      }
    });

    return {
      dataKeys,
      dataTypes: derivedTypes,
      tableData,
      tableTitle: indicatorResponse.title ?? '',
      unresolved: unresolvedObjectCells,
    };
  }, [indicatorResponse]);

  useEffect(() => {
    if (!unresolved || unresolved.length === 0) return;
    console.info('[IndicatorTable] Found non-primitive indicator cells', {
      indicator,
      columns: Array.from(new Set(unresolved.map(cell => cell.column))),
      sample: unresolved.slice(0, 3),
    });
  }, [indicator, unresolved]);

  const numberSortedKeys = useMemo(
    () => dataKeys.filter((c: any) => c !== 'Name' && c !== 'Ticker' && c !== ''),
    [dataKeys],
  );

  return (
    <Wrapper>
      {isLoading ? (
        <SpinnerWrapper>
          <Spinner />
        </SpinnerWrapper>
      ) : (
        <Table
          data={tableData}
          dataKeys={dataKeys}
          title={tableTitle}
          isLoading={isLoading}
          gridHeight={3.5}
          numberSortedKeys={numberSortedKeys}
          isDoubleHeaderHeight={false}
          dataTypes={dataTypes}
        />
      )}
    </Wrapper>
  );
}
